import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { trackPageView } from '@/lib/utils/analytics';
import { readShareArrival } from '@/lib/utm';

// Query params that are single-use credentials, not navigation state — Firebase
// auth action links (password reset, email verification, sign-in-with-link) pass
// these directly in the URL, and /auth/email-handler forwards oobCode on to
// /password-reset the same way. Never let a page-view carry one of these to a
// third party (PostHog, GTM/dataLayer) — the credential is still valid when the
// page loads, before the user has done anything with it.
const SENSITIVE_QUERY_PARAMS = ['oobCode', 'apiKey', 'token'];

function redactSensitiveParams(search: string): string {
    if (!search) return search;
    const params = new URLSearchParams(search);
    let redacted = false;
    for (const key of SENSITIVE_QUERY_PARAMS) {
        if (params.has(key)) {
            params.set(key, 'REDACTED');
            redacted = true;
        }
    }
    return redacted ? `?${params.toString()}` : search;
}

// Fires a $pageview on every SPA route change. PostHog is initialised at module
// load in src/main.tsx WITHOUT the history-autocapture preset, so route changes
// do not emit a $pageview automatically — we send it explicitly here. This runs
// for every visitor (basic cookieless tier by default; upgraded to persistent
// after consent), so it powers page-view + unique-visitor reach measurement.

// The INBOUND half of tracked sharing. buildShareUrl() has tagged our share
// links with utm_source=share for a while; until now nothing read them back,
// so "did sharing bring anyone?" had no row to count -- the exact failure the
// portfolio plays list calls "often built and unfed".
//
// Fires once per page LOAD, not per route change: one arrival is one event,
// and the utm params stay in the address bar while the visitor clicks around.
// Module scope rather than a ref, so a remount cannot double-count it.
let shareArrivalFired = false;

function captureShareArrival(): void {
    if (shareArrivalFired) return;
    shareArrivalFired = true;
    // Fires NOTHING unless the URL actually carries our own utm_source -- an
    // "unknown" bucket that swallowed organic traffic would be worse than no
    // row at all.
    const arrival = readShareArrival(window.location.search);
    if (!arrival) return;
    try {
        posthog.capture('share_visit', arrival);
    } catch {
        /* posthog not initialised (no token) -- nothing to capture */
    }
}

export function usePageTracking(): void {
    const location = useLocation();

    useEffect(() => {
        const safeSearch = redactSensitiveParams(location.search);
        const url = window.location.origin + location.pathname + safeSearch + location.hash;
        trackPageView(url);
        try {
            posthog.capture('$pageview', { $current_url: url });
        } catch {
            /* posthog not initialised (no token) — nothing to capture */
        }
        captureShareArrival();
    }, [location.pathname, location.search, location.hash]);
}
